import { Clock } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface TimePickerProps {
  value?: Date | null
  onChange?: (date: Date) => void
  onSelect?: (e: { value: Date | null }) => void
  disabled?: boolean
  className?: string
  hourFormat?: '12' | '24'
  stepMinute?: number
  stepHour?: number
}

export function TimePicker({
  value,
  onChange,
  onSelect,
  disabled = false,
  className,
  hourFormat = '24',
  stepMinute = 15,
  stepHour = 1,
}: TimePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [selectedTime, setSelectedTime] = React.useState<Date | null>(value instanceof Date ? value : null)

  // Refs for scrolling
  const hourContainerRef = React.useRef<HTMLDivElement>(null)
  const minuteContainerRef = React.useRef<HTMLDivElement>(null)
  const selectedHourRef = React.useRef<HTMLButtonElement>(null)
  const selectedMinuteRef = React.useRef<HTMLButtonElement>(null)

  // Generate hour options
  const hours = React.useMemo(() => {
    const maxHours = hourFormat === '24' ? 24 : 12
    const result: number[] = []
    for (let i = 0; i < maxHours; i += stepHour) {
      result.push(i)
    }
    return result
  }, [hourFormat, stepHour])

  // Generate minute options
  const minutes = React.useMemo(() => {
    const result: number[] = []
    for (let i = 0; i < 60; i += stepMinute) {
      result.push(i)
    }
    return result
  }, [stepMinute])

  const formatTime = (date: Date | null) => {
    if (!date || !(date instanceof Date)) return ''
    const h = date.getHours()
    const m = date.getMinutes()
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
  }

  const handleHourSelect = (hour: number) => {
    const newDate = new Date(selectedTime || new Date())
    newDate.setHours(hour)
    newDate.setSeconds(0)
    newDate.setMilliseconds(0)

    setSelectedTime(newDate)
    if (onChange) {
      onChange(newDate)
    }
    if (onSelect) {
      onSelect({ value: newDate })
    }
    // Don't close the popover, let user select minutes
  }

  const handleMinuteSelect = (minute: number) => {
    const newDate = new Date(selectedTime || new Date())
    newDate.setMinutes(minute)
    newDate.setSeconds(0)
    newDate.setMilliseconds(0)

    setSelectedTime(newDate)
    if (onChange) {
      onChange(newDate)
    }
    if (onSelect) {
      onSelect({ value: newDate })
    }
    // Don't close the popover, let user adjust if needed
  }

  React.useEffect(() => {
    // Only update if value is a Date or null, and avoid infinite loop if both are not Dates
    if (value instanceof Date) {
      if (!selectedTime || !(selectedTime instanceof Date) || value.getTime() !== selectedTime.getTime()) {
        setSelectedTime(value)
      }
    } else if (!value && selectedTime) {
      setSelectedTime(null)
    }
  }, [value, selectedTime])

  // Scroll to selected time when popover opens
  React.useEffect(() => {
    if (isOpen && selectedTime) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (selectedHourRef.current) {
          selectedHourRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
        if (selectedMinuteRef.current) {
          selectedMinuteRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
      }, 100)
    }
  }, [isOpen, selectedTime])

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal',
            !selectedTime && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
        >
          <Clock className="mr-2 h-4 w-4" />
          {selectedTime ? formatTime(selectedTime) : <span>Wähle Zeit</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex w-fit max-w-[240px]">
          {/* Hours */}
          <div className="border-r">
            <div className="border-b p-2 text-center text-sm font-medium">Stunden</div>
            <div ref={hourContainerRef} className="max-h-60 overflow-y-auto">
              {hours.map((hour) => (
                <button
                  key={hour}
                  ref={selectedTime?.getHours() === hour ? selectedHourRef : null}
                  onClick={() => handleHourSelect(hour)}
                  className={cn(
                    'hover:bg-accent hover:text-accent-foreground w-full px-4 py-2 text-sm',
                    selectedTime?.getHours() === hour &&
                      'bg-[#27c226] text-white hover:bg-[#27c226] hover:text-white'
                  )}
                >
                  {hour.toString().padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>
          {/* Minutes */}
          <div>
            <div className="border-b p-2 text-center text-sm font-medium">Minuten</div>
            <div ref={minuteContainerRef} className="max-h-60 overflow-y-auto">
              {minutes.map((minute) => (
                <button
                  key={minute}
                  ref={selectedTime?.getMinutes() === minute ? selectedMinuteRef : null}
                  onClick={() => handleMinuteSelect(minute)}
                  className={cn(
                    'hover:bg-accent hover:text-accent-foreground w-full px-4 py-2 text-sm',
                    selectedTime?.getMinutes() === minute &&
                      'bg-[#27c226] text-white hover:bg-[#27c226] hover:text-white'
                  )}
                >
                  {minute.toString().padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface TimeInputProps {
  value?: Date | null
  disabled?: boolean
  className?: string
}

export function TimeInput({ value, disabled, className }: TimeInputProps) {
  const formatTime = (date: Date | null) => {
    if (!date || !(date instanceof Date)) return ''
    const h = date.getHours()
    const m = date.getMinutes()
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
  }

  return (
    <Input
      type="text"
      value={formatTime(value || null)}
      disabled={disabled}
      readOnly
      className={className}
    />
  )
}
